import { ReactElement, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { AttentionSurface, trackAttentionOpenList, trackAttentionShown } from "../../tracking";
import useAttention from "../../useAttention";
import AttentionRow from "../AttentionRow/AttentionRow";
import "./AttentionBlock.scss";

interface IProps {
    /**
     * How many rows to show before pointing at the full list.
     */
    maxRows?: number;

    /**
     * Which of the two places the block is mounted in. Only used for the tracking,
     * where a bell that is opened and a page that is visited are different things.
     */
    surface: AttentionSurface;
}

const defaultMaxRows = 3;

function AttentionBlock(props: IProps): ReactElement | null {
    const { list, isLoading, hasFailed, reload } = useAttention();
    const maxRows = props.maxRows ?? defaultMaxRows;
    const { surface } = props;

    // Once per mount, not once per list. The list is refetched on a signal and on
    // a poll, and counting those as showings would turn one open bell into a
    // dozen.
    const hasTracked = useRef<boolean>(false);

    useEffect(() => {
        // Nothing waiting is not a showing. Opening the bell on an empty list is
        // somebody reading their notifications, and counting it would put an
        // impression in the denominator that could never have produced a click.
        // What is left is the opens where there was something to act on, which is
        // the only population a click-through rate means anything against.
        if (hasTracked.current || isLoading || !list || !list.applicable || list.count === 0) {
            return;
        }

        hasTracked.current = true;
        trackAttentionShown(surface, list);
    }, [isLoading, list, surface]);

    if (!list || !list.applicable) {
        // Not for this person. An empty list would say "you are done", which is a
        // different thing and not true of someone the surface was never for.
        //
        // This also covers the first load, where there is no list yet and so no way
        // to know whose surface this is. Nothing is the right answer there: a
        // skeleton would flash a Just nu placeholder at every user on every tenant,
        // including the ones who will never have it. After that first load the store
        // holds the list for the rest of the session, so later mounts have it
        // already and there is nothing to wait for.
        return null;
    }

    const rows = list.rows.slice(0, maxRows);
    const isIncomplete = hasFailed || list.unavailableSources.length > 0;
    const hasOverdue = list.rows.some((row) => row.isOverdue);

    return (
        <div className="AttentionBlock">
            <div className="AttentionBlock-header">
                <span className="AttentionBlock-heading">
                    <span className="AttentionBlock-title">{localize("ATTENTION_TITLE")}</span>
                    {list.count > 0 && (
                        <span
                            className={hasOverdue
                                ? "AttentionBlock-counter AttentionBlock-counter--late"
                                : "AttentionBlock-counter"}
                        >
                            {list.count}
                        </span>
                    )}
                </span>

                {list.count > 0 && (
                    <Link
                        className="AttentionBlock-showAll"
                        to="/just-nu"
                        onClick={() => trackAttentionOpenList(surface, list)}
                    >
                        {`${localize("ATTENTION_SHOW_ALL")} ${list.count}`}
                        <Visage2Icon icon="arrow-right-3" size="extra-small" />
                    </Link>
                )}
            </div>

            {isIncomplete && (
                <div className="AttentionBlock-incomplete">
                    {/* Never "Allt klart" when something could not be read: a short
                        list and a broken one look identical otherwise. */}
                    <span className="AttentionBlock-incompleteText">
                        {localize("ATTENTION_INCOMPLETE")}
                    </span>
                    <button className="AttentionBlock-retry" type="button" onClick={reload}>
                        {localize("ATTENTION_RETRY")}
                    </button>
                </div>
            )}

            {list.count === 0 && !isIncomplete && (
                <div className="AttentionBlock-allClear">
                    <span className="AttentionBlock-allClearIcon" aria-hidden="true">
                        <Visage2Icon icon="tick-circle" size="extra-small" />
                    </span>
                    <span className="AttentionBlock-allClearText">
                        {localize("ATTENTION_ALL_CLEAR")}
                    </span>
                </div>
            )}

            {rows.map((row, index) => (
                <AttentionRow
                    key={`${row.sourceType}-${row.sourceItemId}`}
                    row={row}
                    isPrimary={index === 0}
                    surface={surface}
                    position={index}
                />
            ))}
        </div>
    );
}

export default AttentionBlock;
